import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Badge, Spinner, Alert, Button, Row, Col } from 'react-bootstrap';
import api from '../../services/api';
import { useInterfaceStore } from '../../stores/useInterfaceStore';
import { FiArrowLeft, FiPrinter } from '../../utils/icons';
import * as maturidade from '../../utils/maturidade';
import type { ChecklistItemResposta, ChecklistRequisitoPeso, PerguntaPeso } from '../../utils/maturidade';

// ── Tipos (só os campos que este relatório realmente usa) ────────────────────

interface QP extends PerguntaPeso {
  pergunta_id: number;
  pergunta_text: string;
  tipo_resposta: 'SIM_NAO' | 'PERCENTUAL' | 'TEXTO_LIVRE' | 'MULTIPLA_ESCOLHA';
  checklist_id: number | null;
  nivel: 'principal' | 'complementar';
  ordem: number;
}

interface ChecklistItemDef {
  id: number;
  descricao: string;
  percentual_peso?: number;
  ativo?: boolean;
}

interface Constatacao {
  qp: QP;
  posicaoOriginal: number; // 1-indexada, ordem original do questionário
  resposta: any;
  maturidadePct: number | null; // null = N/A
  classificacaoNome: string | null;
  comentarioAuditor: string | null;
  itensNao: { descricao: string; comPlano: boolean }[];
}

// Severidade: mais grave primeiro. "Observação" = "Oportunidade de Melhoria" do
// documento da SM (mesmo cadastro auxiliar já existente, só nome diferente).
// Classifica por PALAVRA-CHAVE, não por igualdade exata de string: o cadastro de
// Classificação de Conformidade é editável por empresa/ambiente — confirmado na prática
// que o valor "Conforme" da seed original já aparece como "Em Conformidade" num dos
// ambientes — então casar pelo nome exato quebraria a ordenação e as cores silenciosamente
// sempre que alguém renomear o cadastro em qualquer empresa.
const MARCAS_COMBINANTES = new RegExp('[̀-ͯ]', 'g');
const normalizar = (s: string): string =>
  s.normalize('NFD').replace(MARCAS_COMBINANTES, '').toLowerCase();

const rankSeveridade = (nome: string | null): number => {
  if (nome == null) return 5;
  const n = normalizar(nome);
  if (n.includes('maior')) return 0;
  if (n.includes('menor')) return 1;
  if (n.includes('observa') || n.includes('melhoria')) return 2;
  if (n.includes('conform')) return 3; // "Conforme", "Em Conformidade", etc.
  return 4; // classificação cadastrada mas fora do vocabulário reconhecido
};

const getBarVariant = (pct: number | null) => {
  if (pct === null) return 'secondary';
  if (pct >= 80) return 'success';
  if (pct >= 60) return 'warning';
  return 'danger';
};

const RelatorioAuditoriaPage: React.FC = () => {
  const { questionarioId } = useParams<{ questionarioId: string }>();
  const navigate = useNavigate();
  const empresaId = useInterfaceStore((state) => state.empresaId);

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [nomeEmpresa, setNomeEmpresa] = useState('');
  const [questionario, setQuestionario] = useState<{ nome: string; framework?: string } | null>(null);
  const [perguntas, setPerguntas] = useState<QP[]>([]);
  const [answers, setAnswers] = useState<Record<number, any>>({});
  const [classificacoes, setClassificacoes] = useState<Record<number, string | null>>({});
  const [comentarios, setComentarios] = useState<Record<number, string | null>>({});
  const [checklistAnswers, setChecklistAnswers] = useState<Record<number, Record<number, ChecklistItemResposta>>>({});
  const [checklistPlanos, setChecklistPlanos] = useState<Record<number, Record<number, string | null>>>({});
  const [checklistData, setChecklistData] = useState<Record<number, ChecklistRequisitoPeso[]>>({});
  const [checklistItensById, setChecklistItensById] = useState<Record<number, Record<number, ChecklistItemDef>>>({});

  useEffect(() => {
    if (!questionarioId) return;
    const id = Number(questionarioId);
    setLoading(true);
    setErro(null);

    const load = async () => {
      const [qRes, aRes] = await Promise.all([
        api.getQuestionnaire(id),
        api.getAnswers(id), // sem usuarioId = modo Auditoria: respostas de todos que responderam
      ]);

      if (!qRes.success || !qRes.data) {
        setErro('Questionário não encontrado.');
        setLoading(false);
        return;
      }

      const q = qRes.data as any;
      setQuestionario({ nome: q.nome, framework: q.framework });
      const qps: QP[] = ((q.perguntas || []) as QP[]).slice().sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
      setPerguntas(qps);

      const existingAnswers: Record<number, any> = {};
      const existingClassif: Record<number, string | null> = {};
      const existingComentario: Record<number, string | null> = {};
      const existingChecklistAnswers: Record<number, Record<number, ChecklistItemResposta>> = {};
      const existingChecklistPlanos: Record<number, Record<number, string | null>> = {};

      if (aRes.success && Array.isArray(aRes.data)) {
        (aRes.data as any[]).forEach(r => {
          const v = r.resposta_texto ?? r.resposta_percentual;
          if (v !== undefined && v !== null) existingAnswers[r.questionario_pergunta_id] = v;
          existingClassif[r.questionario_pergunta_id] = r.classificacao_conformidade_nome ?? null;
          existingComentario[r.questionario_pergunta_id] = r.comentario_auditor ?? null;
          if (r.checklist_respostas) {
            const respMap: Record<number, ChecklistItemResposta> = {};
            const planoMap: Record<number, string | null> = {};
            Object.entries(r.checklist_respostas as Record<string, any>).forEach(([itemId, det]: [string, any]) => {
              if (det?.resposta) respMap[Number(itemId)] = det.resposta;
              if (det?.plano_descricao) planoMap[Number(itemId)] = det.plano_descricao;
            });
            if (Object.keys(respMap).length > 0) existingChecklistAnswers[r.questionario_pergunta_id] = respMap;
            if (Object.keys(planoMap).length > 0) existingChecklistPlanos[r.questionario_pergunta_id] = planoMap;
          }
        });
      }
      setAnswers(existingAnswers);
      setClassificacoes(existingClassif);
      setComentarios(existingComentario);
      setChecklistAnswers(existingChecklistAnswers);
      setChecklistPlanos(existingChecklistPlanos);

      // Carrega a definição (pesos + descrições) dos checklists usados no questionário —
      // necessária tanto pro cálculo de maturidade quanto pra exibir o texto dos itens NÃO.
      const qpsComChecklist = qps.filter(p => p.checklist_id);
      if (qpsComChecklist.length > 0) {
        const results = await Promise.all(qpsComChecklist.map(p => api.getChecklistCompleto(p.checklist_id!)));
        const newChecklistData: Record<number, ChecklistRequisitoPeso[]> = {};
        const newItensById: Record<number, Record<number, ChecklistItemDef>> = {};
        qpsComChecklist.forEach((p, i) => {
          const r = results[i];
          const requisitos = (r.success && r.data) ? ((r.data as any).requisitos || []) : [];
          newChecklistData[p.qp_id] = requisitos;
          const porId: Record<number, ChecklistItemDef> = {};
          requisitos.forEach((req: any) => (req.itens || []).forEach((item: ChecklistItemDef) => { porId[item.id] = item; }));
          newItensById[p.qp_id] = porId;
        });
        setChecklistData(newChecklistData);
        setChecklistItensById(newItensById);
      }

      setLoading(false);
    };

    load().catch(() => { setErro('Erro ao carregar o relatório.'); setLoading(false); });
  }, [questionarioId]);

  useEffect(() => {
    if (!empresaId) { setNomeEmpresa(''); return; }
    api.getCompany(empresaId).then(res => {
      if (res.success && res.data) setNomeEmpresa(res.data.nome_fantasia || res.data.razao_social || '');
    });
  }, [empresaId]);

  const constatacoes: Constatacao[] = useMemo(() => {
    const lista: Constatacao[] = perguntas.map((qp, idx) => {
      const requisitos = checklistData[qp.qp_id];
      const respostasItem = checklistAnswers[qp.qp_id] || {};
      const checklistPct = maturidade.getChecklistPct(qp, requisitos, respostasItem);
      const v = answers[qp.qp_id];

      let maturidadePct: number | null;
      if (v === 'NA') maturidadePct = null;
      else if (checklistPct !== null) maturidadePct = checklistPct;
      else if (v === 'SIM') maturidadePct = 100;
      else if (v === 'NAO') maturidadePct = 0;
      else if (typeof v === 'number') maturidadePct = v;
      else if (v) maturidadePct = 50;
      else maturidadePct = null;

      const itensPorId = checklistItensById[qp.qp_id] || {};
      const planosQp = checklistPlanos[qp.qp_id] || {};
      const itensNao = Object.entries(respostasItem)
        .filter(([, resp]) => resp === 'NAO')
        .map(([itemId]) => {
          const item = itensPorId[Number(itemId)];
          return { descricao: item?.descricao || `Item ${itemId}`, comPlano: !!planosQp[Number(itemId)] };
        });

      return {
        qp,
        posicaoOriginal: idx + 1,
        resposta: v,
        maturidadePct,
        classificacaoNome: classificacoes[qp.qp_id] ?? null,
        comentarioAuditor: comentarios[qp.qp_id] ?? null,
        itensNao,
      };
    });

    return lista
      .map((c, i) => ({ c, i })) // mantém a ordem original como critério de desempate estável
      .sort((a, b) => {
        const diff = rankSeveridade(a.c.classificacaoNome) - rankSeveridade(b.c.classificacaoNome);
        return diff !== 0 ? diff : a.i - b.i;
      })
      .map(({ c }) => c);
  }, [perguntas, answers, classificacoes, comentarios, checklistAnswers, checklistData, checklistItensById, checklistPlanos]);

  const respondidas: PerguntaPeso[] = useMemo(
    () => perguntas.filter(p => answers[p.qp_id] !== undefined && answers[p.qp_id] !== null && answers[p.qp_id] !== ''),
    [perguntas, answers]
  );
  const maturidadeGeral = useMemo(
    () => maturidade.calcMaturity(respondidas, answers, checklistData, checklistAnswers),
    [respondidas, answers, checklistData, checklistAnswers]
  );

  if (loading) return <Container className="py-5 text-center"><Spinner animation="border" /></Container>;
  if (erro) return <Container className="py-4"><Alert variant="danger">{erro}</Alert></Container>;

  return (
    <Container fluid className="py-3 py-md-4">
      <div className="d-flex align-items-center justify-content-between mb-3 no-print">
        <Button variant="outline-secondary" size="sm" onClick={() => navigate('/app/relatorios')}>
          <FiArrowLeft /> Voltar
        </Button>
        <Button variant="primary" size="sm" onClick={() => window.print()}>
          <FiPrinter /> Exportar PDF
        </Button>
      </div>

      <div className="print-area">
        <div className="text-center mb-4">
          <h2 className="mb-0">Relatório de Auditoria</h2>
          <div className="text-muted">
            {questionario?.nome}{questionario?.framework ? ` - ${questionario.framework}` : ''}
          </div>
        </div>

        <Row className="mb-4 align-items-center">
          <Col xs={4}>
            <div className="text-muted small">Empresa</div>
            <div className="fw-medium">{nomeEmpresa || '—'}</div>
          </Col>
          <Col xs={4}>
            <div className="text-muted small">Emitido em</div>
            <div className="fw-medium">{new Date().toLocaleDateString('pt-BR')}</div>
          </Col>
          <Col xs={2}>
            <div className="text-muted small">Total de Constatações</div>
            <div className="fw-medium">{constatacoes.length}</div>
          </Col>
          <Col xs={2} className="text-end">
            <div className="text-muted small">Maturidade</div>
            <Badge bg={getBarVariant(maturidadeGeral)} className="fs-6">{maturidadeGeral.toFixed(1)}%</Badge>
          </Col>
        </Row>

        {constatacoes.length === 0 ? (
          <Alert variant="info">Este questionário não tem perguntas.</Alert>
        ) : (
          constatacoes.map((c, i) => (
            <div
              key={c.qp.qp_id}
              className="print-break-inside-avoid mb-3 p-3"
              style={{ border: '1px solid #dee2e6', borderLeft: `4px solid var(--bs-${getBarVariant(c.maturidadePct)})`, borderRadius: 6 }}
            >
              <div className="d-flex justify-content-between align-items-start mb-2">
                <span className="fw-bold text-uppercase small text-muted">Constatação {String(i + 1).padStart(2, '0')}</span>
                <span className="text-muted small">Pergunta {c.posicaoOriginal} de {perguntas.length}</span>
              </div>

              <Row className="mb-2">
                <Col md={8}>
                  <div className="text-muted small">Pergunta</div>
                  <div className="fw-medium">{c.qp.pergunta_text}</div>
                </Col>
                <Col md={4} className="text-md-end">
                  <div className="text-muted small">Nível Maturidade</div>
                  <Badge bg={getBarVariant(c.maturidadePct)}>{c.maturidadePct === null ? 'N/A' : `${c.maturidadePct.toFixed(0)}%`}</Badge>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col md={6}>
                  <div className="text-muted small">Resposta</div>
                  <Badge bg={c.resposta === 'SIM' ? 'success' : c.resposta === 'NAO' ? 'danger' : 'secondary'}>
                    {c.resposta === 'SIM' ? 'Sim' : c.resposta === 'NAO' ? 'Não' : c.resposta === 'NA' ? 'N/A'
                      : typeof c.resposta === 'number' ? `${c.resposta}%` : (c.resposta || '—')}
                  </Badge>
                </Col>
                <Col md={6}>
                  <div className="text-muted small">Classificação da Conformidade</div>
                  <div>
                    {c.classificacaoNome
                      ? <Badge bg={rankSeveridade(c.classificacaoNome) <= 1 ? 'danger' : rankSeveridade(c.classificacaoNome) === 2 ? 'warning' : 'success'}>{c.classificacaoNome}</Badge>
                      : <span className="text-muted">—</span>}
                  </div>
                </Col>
              </Row>

              {c.itensNao.length > 0 && (
                <div className="mb-2">
                  <div className="text-muted small">Itens do Checklist marcados como NÃO</div>
                  <ul className="mb-0 ps-3">
                    {c.itensNao.map((item, idx) => (
                      <li key={idx}>
                        {item.descricao}
                        {item.comPlano && <span className="text-muted ms-2 small">Com Plano de ação</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <div className="text-muted small">Comentários do Auditor</div>
                <div className="p-2" style={{ background: 'var(--cp-surface-alt)', borderRadius: 4 }}>
                  {c.comentarioAuditor || <span className="text-muted">Sem comentários.</span>}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </Container>
  );
};

export default RelatorioAuditoriaPage;
